import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Gift, Star, Clock } from 'lucide-react';

const PromoCodePopup = ({ isOpen, onClose, userInfo, onPromoGenerated }) => {
  const [formData, setFormData] = useState({
    name: userInfo?.name || '',
    email: userInfo?.email || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/promo/generate-signup-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('dcTech_token')}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPromoResult(data);
        if (onPromoGenerated) {
          onPromoGenerated(data);
        }
      } else {
        setError(data.detail || data.message || 'Failed to generate promo code');
      }
    } catch (err) {
      console.error('Promo generation error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-6 rounded-t-xl relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80  transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
          
          <div className="text-center">
            <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Gift size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">🎉 Welcome Bonus!</h2>
            <p className="text-white/90 text-sm">
              Claim your exclusive promo code for free Basic plan access
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!promoResult ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <Star className="text-blue-600 mt-0.5" size={20} />
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-1">
                      🎁 6 Months of Free Basic Plan
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>✅ Unlimited service listings</li>
                      <li>✅ Job bidding capabilities</li>
                      <li>✅ Customer messaging</li>
                      <li>✅ Secure payment processing</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="promoName" className="text-gray-700">Full Name</Label>
                  <Input
                    id="promoName"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter your full name"
                    className="mt-1"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <Label htmlFor="promoEmail" className="text-gray-700">Email Address</Label>
                  <Input
                    id="promoEmail"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter your email address"
                    className="mt-1"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                  disabled={isLoading}
                >
                  Skip for Now
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating...' : 'Get My Promo Code'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-300 rounded-lg p-6">
                <div className="text-green-600 mb-3">
                  <Gift size={48} className="mx-auto" />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">
                  Promo Code Generated! 🎉
                </h3>
                
                <div className="bg-white border-2 border-green-300 rounded-lg p-4 mb-4">
                  <div className="text-2xl font-mono font-bold text-green-700 tracking-wider">
                    {promoResult.code}
                  </div>
                </div>

                <div className="text-sm text-green-700 space-y-2">
                  <p className="flex items-center justify-center space-x-2">
                    <Clock size={16} />
                    <span><strong>{promoResult.durationMonths} months</strong> of free Basic plan access</span>
                  </p>
                  <p>📧 Promo code has been sent to your email!</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm text-yellow-800">
                <p className="font-semibold mb-1">⏰ Important:</p>
                <p>Your promo code expires in 30 days. Redeem it in your subscription settings to activate your free plan.</p>
              </div>

              <Button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white"
              >
                Continue to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoCodePopup;